import React, { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "../../../shared/ui/Modal";
import Button from "../../../shared/ui/Button";
import { getPlatforms } from "../../../i18n";
import { useTranslation } from "../../../i18n";

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const DocIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    className="text-[#006666]"
    aria-hidden
  >
    <path
      d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M14 2v6h6M10 13h8M10 17h6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const PlusCircleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="10" stroke="#cbd5e1" strokeWidth="1.4" />
    <path
      d="M12 8v8M8 12h8"
      stroke="#64748b"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(platformId: string) => void} props.onConnect
 * @param {string[] | null} [props.allowedPlatformIds]
 */
const ConnectPlatformModal = ({ open, onClose, onConnect, allowedPlatformIds }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [selected, setSelected] = useState(null);

  const allPlatforms = useMemo(() => getPlatforms(t), [t]);

  const visiblePlatforms = useMemo(() => {
    if (!allowedPlatformIds?.length) return allPlatforms;
    const allow = new Set(allowedPlatformIds);
    return allPlatforms.filter((p) => allow.has(p.id));
  }, [allowedPlatformIds, allPlatforms]);

  useEffect(() => {
    if (!open) return;
    setSelected(visiblePlatforms.length === 1 ? visiblePlatforms[0].id : null);
  }, [open, visiblePlatforms]);

  const handleOpenChange = (next) => {
    if (!next) {
      setSelected(null);
      onClose?.();
    }
  };

  const cardsGridClass =
    visiblePlatforms.length === 1
      ? "mx-auto grid w-full max-w-[400px] grid-cols-1 gap-4"
      : "grid gap-4 sm:grid-cols-2";

  return (
    <Modal open={open} onClose={() => handleOpenChange(false)} labelledBy={titleId}>
      <button
        type="button"
        onClick={() => handleOpenChange(false)}
        className="absolute right-5 top-5 rounded-lg p-2 text-[#64748b] transition hover:bg-[#f1f5f9] hover:text-[#0f172a]"
        aria-label={t("common.close")}
      >
        <CloseIcon />
      </button>

      <div className="px-8 pb-6 pt-10 text-center md:px-12 md:pt-12">
        <h2
          id={titleId}
          className="text-[22px] font-bold tracking-tight text-[#0f172a] md:text-[26px]"
        >
          {t("connectPlatform.title")}
        </h2>
        <p className="mx-auto mt-3 max-w-[520px] text-[14px] leading-relaxed text-[#6b7280] md:text-[15px]">
          {t("connectPlatform.subtitle")}
        </p>
      </div>

      <div className={`px-6 pb-2 md:px-10 ${cardsGridClass}`}>
        {visiblePlatforms.map(({ id, title, description, badge, Logo }) => {
          const isSel = selected === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setSelected(id)}
              className={`relative flex w-full flex-col rounded-[14px] border-2 bg-[#f8fafc] p-5 text-left transition ${
                isSel
                  ? "border-[#006666] shadow-[0_8px_24px_rgba(0,102,102,0.12)]"
                  : "border-transparent ring-1 ring-[#e5e7eb] hover:ring-[#cbd5e1]"
              }`}
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <Logo />
                <PlusCircleIcon />
              </div>
              <span className="text-[16px] font-bold text-[#0f172a]">{title}</span>
              <p className="mt-2 flex-1 text-[13px] leading-[1.55] text-[#64748b]">
                {description}
              </p>
              <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#006666]">
                {badge}
              </p>
            </button>
          );
        })}
      </div>

      <div className="mt-4 rounded-b-[20px] border-t border-[#eef2f6] bg-[#fafbfc] px-6 py-5 md:px-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/dashboard/client/documentation"
            onClick={() => handleOpenChange(false)}
            className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#006666] transition hover:text-[#005252]"
          >
            <DocIcon />
            {t("common.documentation")}
          </Link>
          <div className="flex flex-wrap items-center justify-end gap-3 sm:gap-4">
            <Button
              type="button"
              variant="ghost"
              className="!h-auto !w-auto shrink-0 px-2 py-2"
              onClick={() => handleOpenChange(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              className="!h-12 !w-auto min-w-[180px] shrink-0 rounded-full px-8 !text-[15px]"
              disabled={!selected}
              onClick={() => {
                onConnect?.(selected);
                handleOpenChange(false);
              }}
            >
              {t("connectPlatform.connect")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ConnectPlatformModal;
